import React, { createContext, useContext, useState, useEffect } from 'react';

interface TourContextType {
  showWelcomeTour: boolean;
  setShowWelcomeTour: (show: boolean) => void;
  activeTour: string | null;
  setActiveTour: (tour: string | null) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  hasSeenWelcome: boolean;
  markWelcomeSeen: () => void;
  startTour: (tourId: string) => void; // Helper to start tour
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [activeTour, setActiveTour] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenWelcome, setHasSeenWelcome] = useState(false);

  useEffect(() => {
    // Check if user has seen the welcome tour
    const hasSeenTour = localStorage.getItem('hasSeenWelcomeTour');
    
    if (!hasSeenTour) {
      // Show welcome popup after a short delay
      const timer = setTimeout(() => {
        setShowWelcomeTour(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setHasSeenWelcome(true);
    }
  }, []);

  const markWelcomeSeen = () => {
    localStorage.setItem('hasSeenWelcomeTour', 'true');
    setHasSeenWelcome(true);
    setShowWelcomeTour(false);
  };

  const startTour = (tourId: string) => {
    console.log('🚀 Starting tour:', tourId);
    // Use functional updates to ensure latest state
    setActiveTour(prev => {
      console.log('🔄 setActiveTour updating from', prev, 'to', tourId);
      return tourId;
    });
    setCurrentStep(prev => {
      console.log('🔄 setCurrentStep updating from', prev, 'to 0');
      return 0;
    });
    console.log('✅ Tour state update calls completed');
  };

  // Debug: Log activeTour changes
  useEffect(() => {
    console.log('🔄 Active tour changed:', activeTour);
  }, [activeTour]);

  useEffect(() => {
    console.log('🔄 Current step changed:', currentStep);
  }, [currentStep]);

  return (
    <TourContext.Provider
      value={{
        showWelcomeTour,
        setShowWelcomeTour,
        activeTour,
        setActiveTour,
        currentStep,
        setCurrentStep,
        hasSeenWelcome,
        markWelcomeSeen,
        startTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
